import { datadogRum } from '@datadog/browser-rum';

/**
 * Detects the environment from the browser URL.
 * 
 * @returns The detected environment: 'skunkworks', 'dev', 'staging', 'production', or 'local'
 */
function detectEnvironmentFromUrl(): 'skunkworks' | 'dev' | 'staging' | 'production' | 'local' {
  if (typeof window === 'undefined') {
    return 'local';
  }

  const hostname = window.location.hostname.toLowerCase();
  
  if (hostname.includes('skunkworks.aipx.co')) {
    return 'skunkworks';
  } else if (hostname.includes('next-dev.aipx.co')) {
    return 'dev';
  } else if (hostname.includes('nextstg.aipx.co')) {
    return 'staging';
  } else if (hostname.includes('next.aipx.co')) {
    return 'production';
  }
  
  return 'local';
}

/**
 * Gets the API hostname patterns for the current environment.
 * These patterns are used to determine which URLs should have tracing headers injected.
 * 
 * @param env - The detected environment
 * @returns Array of hostname patterns that should be traced
 */
function getApiHostnamePatterns(env: string): string[] {
  // Define API hostname patterns for each environment based on actual config files
  const patterns: Record<string, string[]> = {
    production: ['api.aipx.co'],
    staging: ['apistg.aipx.co'],
    dev: ['api-dev.aipx.co'],
    skunkworks: ['sk-api.aipx.co'],
    local: ['localhost:3001', '127.0.0.1:3001'],
  };

  return patterns[env] || patterns.local;
}

/**
 * Checks if the application is running on localhost.
 * 
 * @returns True if running on localhost, 127.0.0.1, private IP ranges, or .local domains
 */
function isLocalhost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' ||
         hostname.startsWith('192.168.') ||
         hostname.startsWith('10.') ||
         hostname.includes('.local');
}

/**
 * Gets the Datadog RUM configuration.
 * Environment variables can override the default credentials.
 * 
 * @param env - The detected environment (not currently used but kept for future extensibility)
 * @returns Configuration object with applicationId, clientToken, and site
 */
function getDatadogConfig(env: string) {
  const runtimeEnv = typeof window !== 'undefined' ? (window as any).__ENV : {};

  const defaultConfig = {
    applicationId: '55277cab-fc3f-4821-a0fe-7c7b9009799a',
    clientToken: 'pubb25ea22ca524b008298978fdc14d9cd5',
    site: 'us5.datadoghq.com',
  };

  return {
    applicationId: runtimeEnv.NEXT_PUBLIC_DD_RUM_APPLICATION_ID ||
                   runtimeEnv.NEXT_PUBLIC_DD_APPLICATION_ID ||
                   defaultConfig.applicationId,
    clientToken: runtimeEnv.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN ||
                 runtimeEnv.NEXT_PUBLIC_DD_CLIENT_TOKEN ||
                 defaultConfig.clientToken,
    site: runtimeEnv.NEXT_PUBLIC_DD_SITE || defaultConfig.site,
  };
}

/**
 * Gets sampling rates based on whether running on localhost.
 * Localhost uses reduced sampling to minimize data volume.
 * 
 * @param env - The detected environment (not currently used but kept for future extensibility)
 * @returns Object with sessionSampleRate, sessionReplaySampleRate, and traceSampleRate
 */
function getSamplingRates(env: string) {
  if (isLocalhost()) {
    return {
      //sessionSampleRate: 100, If you want to test local datadog video open and set to 100 
      //sessionReplaySampleRate: 100, If you want to test local datadog video open and set to 100 
      sessionSampleRate: 10,
      sessionReplaySampleRate: 5,
      traceSampleRate: 10,
    };
  }

  return {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    traceSampleRate: 20,
  };
}

/**
 * Initializes Datadog RUM (Real User Monitoring).
 * Should be called once on the client side when the app loads.
 * Automatically detects environment from URL and applies appropriate sampling rates.
 */
export function initDatadogRUM() {
  if (typeof window === 'undefined') {
    return;
  }

  const detectedEnv = detectEnvironmentFromUrl();
  const config = getDatadogConfig(detectedEnv);
  const samplingRates = getSamplingRates(detectedEnv);

  if (!config.applicationId || !config.clientToken) {
    console.warn('Datadog RUM: Application ID or Client Token not configured. Skipping initialization.');
    return;
  }

  // Read version from process.env (inlined at build time by Next.js via the
  // Dockerfile ARG/ENV NEXT_PUBLIC_DD_VERSION). This mirrors the sidebar
  // (src/components/nav/Sidebar.tsx) and matches the --release-version value
  // used by datadog-ci sourcemaps upload in cicd-deploy.yaml, so RUM sessions
  // line up with the uploaded sourcemap release. window.__ENV is intentionally
  // not used here because entrypoint.sh does not inject this key.
  const version = process.env.NEXT_PUBLIC_DD_VERSION || '1.0.0_local';

  try {
    // Get API hostname patterns for trace correlation
    const apiHostnamePatterns = getApiHostnamePatterns(detectedEnv);
    
    // Build allowedTracingUrls array with URL patterns
    // Supports both string patterns and RegExp patterns
    const allowedTracingUrls: (string | RegExp)[] = apiHostnamePatterns.map(pattern => {
      if (pattern.includes(':')) {
        const [host, port] = pattern.split(':');
        return new RegExp(`^https?://${host.replace(/\./g, '\\.')}(:${port})?(/.*)?$`, 'i');
      }
      const escapedHost = pattern.replace(/\./g, '\\.');
      return new RegExp(`^https?://([^/]+\\.)?${escapedHost}(/.*)?$`, 'i');
    });

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: 'aipx-app-internal',
      env: detectedEnv,
      version: version,
      sessionSampleRate: samplingRates.sessionSampleRate,
      sessionReplaySampleRate: samplingRates.sessionReplaySampleRate,
      traceSampleRate: samplingRates.traceSampleRate,
      allowedTracingUrls: allowedTracingUrls,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      beforeSend: (event) => {
        // Drop the narrow VHS (@videojs/http-streaming) race that surfaces as
        // "InvalidStateError: ... SourceBuffer has been removed from the
        // parent media source." It originates inside the library's
        // SourceUpdater on dispose/source-swap, is benign for the user, and
        // is mitigated by player.reset() in VideoJSPlayer.tsx. See Datadog
        // issue 48e370f6-fa93-11f0-9885-da7ad0900000.
        if (event.type === 'error') {
          const err: any = (event as any).error ?? {};
          const message: string = typeof err.message === 'string' ? err.message : '';
          const stack: string = typeof err.stack === 'string' ? err.stack : '';
          const matchesSourceBufferRemoved =
            message.includes('SourceBuffer has been removed from the parent media source');
          const matchesVhsStack = /http-streaming|source-updater|segment-loader/i.test(stack);
          if (matchesSourceBufferRemoved || matchesVhsStack) {
            return false;
          }
        }
        return true;
      },
    });

    console.log(`Datadog RUM initialized successfully for environment: ${detectedEnv}, version: ${version}, sampling: ${samplingRates.sessionSampleRate}%/${samplingRates.sessionReplaySampleRate}%/${samplingRates.traceSampleRate}%, API patterns: ${apiHostnamePatterns.join(', ')}`);
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
}

/**
 * Sets user context in Datadog RUM.
 * Call this when user logs in or user information changes.
 * 
 * @param user - User object containing identification and metadata
 */
export function setDatadogUser(user: {
  id?: string | number | null;
  email?: string | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  externalUserId?: string | null;
  locale?: string | null;
  role?: string | null;
  position?: string | null;
  status?: string | null;
  is_operator?: boolean | null;
  tags?: any;
  createdAt?: string | null;
  updatedAt?: string | null;
  teamId?: string | number | null;
}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    datadogRum.setUser({
      id: user.id?.toString() || undefined,
      email: user.email || undefined,
      name: user.username || `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined,
      ...(user.firstName && { firstName: user.firstName }),
      ...(user.lastName && { lastName: user.lastName }),
      ...(user.externalUserId && { externalUserId: user.externalUserId.toString() }),
      ...(user.locale && { locale: user.locale }),
      ...(user.role && { role: user.role }),
      ...(user.position && { position: user.position }),
      ...(user.status && { status: user.status }),
      ...(user.is_operator !== null && { is_operator: user.is_operator }),
      ...(user.teamId && { teamId: user.teamId.toString() }),
    });
  } catch (error) {
    console.error('Failed to set Datadog user:', error);
  }
}

/**
 * Adds a custom action/event to Datadog RUM.
 * 
 * @param name - The name of the action/event
 * @param context - Optional context object with additional metadata
 */
export function addDatadogAction(name: string, context?: Record<string, any>) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    datadogRum.addAction(name, context);
    console.log('Datadog action reported:', name, context);
  } catch (error) {
    console.error('Failed to add Datadog action:', error);
  }
}

/**
 * Adds an error to Datadog RUM for tracking and monitoring.
 * 
 * @param error - The Error object to track
 * @param context - Optional context with source and tags
 */
export function addDatadogError(error: Error, context?: { source?: string; tags?: Record<string, string> }) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (e) {
    console.error('Failed to add Datadog error:', e);
  }
}

/**
 * Clears user context in Datadog RUM.
 * Call this when user logs out to prevent data leakage between sessions.
 */
export function clearDatadogUser() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    datadogRum.clearUser();
  } catch (error) {
    console.error('Failed to clear Datadog user:', error);
  }
}

/**
 * Adds a global context property to all RUM events.
 * Useful for adding environment-wide metadata that should be included in every event.
 *
 * @param key - The property key
 * @param value - The property value
 */
export function addDatadogGlobalContext(key: string, value: any) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    datadogRum.setGlobalContextProperty(key, value);
  } catch (error) {
    console.error('Failed to set Datadog global context:', error);
  }
}

/**
 * Forces the current session to start being recorded as a session replay,
 * regardless of the configured sessionReplaySampleRate.
 * Call this when a user-triggered event (e.g. bug report) makes recording important.
 */
export function startDatadogSessionReplay() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    datadogRum.startSessionReplayRecording();
  } catch (error) {
    console.error('Failed to start Datadog session replay recording:', error);
  }
}

/**
 * Returns the current Datadog RUM session ID and a direct link to the session replay.
 * The replay link can be used to navigate directly to the recording in Datadog.
 *
 * @returns Object with sessionId and replayUrl, or null values if unavailable
 */
export function getDatadogSessionInfo(): { sessionId: string | null; replayUrl: string | null } {
  if (typeof window === 'undefined') {
    return { sessionId: null, replayUrl: null };
  }

  let sessionId: string | null = null;
  try {
    const context = datadogRum.getInternalContext();
    sessionId = context?.session_id ?? null;
  } catch (error) {
    console.error('Failed to get Datadog session info:', error);
    return { sessionId: null, replayUrl: null };
  }

  if (!sessionId) {
    return { sessionId: null, replayUrl: null };
  }

  let replayUrl: string | null = null;
  try {
    // getSessionReplayLink is available in @datadog/browser-rum ≥ 4.6
    if (typeof (datadogRum as any).getSessionReplayLink === 'function') {
      replayUrl = (datadogRum as any).getSessionReplayLink() ?? null;
    }
  } catch (error) {
    console.error('Failed to get Datadog replay URL:', error);
    // replayUrl stays null; sessionId is still returned below
  }

  return { sessionId, replayUrl };
}

